import { Editor } from '@tiptap/core';

/**
 * Check if a landscape section can be inserted at the current cursor position.
 *
 * Returns false if:
 * - Document is in landscape mode
 * - Cursor is inside a table, list, section, or other nested structure
 * - Cursor is not at root level
 */
export const canInsertLandscapeSection = (editor: Editor): boolean => {
  if (editor.storage.section?.isLandscape) return false;

  const { $from } = editor.state.selection;

  // Check if we're inside any node that shouldn't contain sections
  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    const nodeName = node.type.name;

    // These node types cannot contain sections (explicit check for clarity,
    // though depth check below would also catch most cases)
    if (
      [
        'table',
        'tableRow',
        'tableCell',
        'tableHeader',
        'section',
        'listItem',
        'orderedList',
        'bulletList',
        'taskList',
        'taskItem',
      ].includes(nodeName)
    ) {
      return false;
    }
  }

  // Must be at root level (depth 1 = inside document, not nested)
  // depth 0 = document itself, depth 1 = direct children of document
  // Note: depth 0 is theoretically possible but not practically reachable
  // in normal editor state (cursor is always inside a block at depth >= 1)
  return $from.depth <= 1;
};

export default canInsertLandscapeSection;
